import React, { useState, useEffect } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import useMuscleGroups from '../../hooks/useMuscleGroups';

/**
 * Componente per selezionare gruppi muscolari
 * Supporta selezione singola o multipla, con gerarchia categoria → muscolo
 * 
 * @param {number|Array<number>} value - ID muscolo selezionato (singolo) o array di ID (multiplo)
 * @param {function} onChange - Callback chiamata quando cambia la selezione
 * @param {string} mode - Modalità: 'single' (default) | 'multi'
 * @param {boolean} primaryOnly - Se true, mostra solo muscoli primari (default: false)
 * @param {string} category - Filtra per categoria specifica (opzionale)
 * @param {boolean} showHierarchy - Se true, mostra gerarchia Categoria → Muscoli (default: true)
 * @param {string} placeholder - Placeholder per il campo di selezione
 * @param {boolean} required - Se true, il campo è obbligatorio
 * @param {string} label - Label del campo
 * @param {string} className - Classi CSS aggiuntive
 * @param {'default'|'gym'} variant - 'gym' allinea colori a modali palestra (#484080, #dad8e5, ecc.)
 */
const MuscleGroupSelector = ({
  value,
  onChange,
  mode = 'single',
  primaryOnly = false,
  category = null,
  showHierarchy = true,
  placeholder = 'Seleziona muscolo...',
  required = false,
  label = null,
  className = '',
  variant = 'default'
}) => {
  const isGym = variant === 'gym';
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const { muscles, loading, getMusclesByCategory, getCategories, getMusclesGroupedByCategory } = useMuscleGroups();
  
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState(category || '');

  // Filtra i muscoli in base alla categoria e alla ricerca
  const filteredMuscles = React.useMemo(() => {
    let result = muscles;

    // Filtra per categoria se specificata
    if (selectedCategory) {
      result = getMusclesByCategory(selectedCategory);
    }

    // Filtra per termine di ricerca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(m => 
        m.name.toLowerCase().includes(term) ||
        (m.name_en && m.name_en.toLowerCase().includes(term))
      );
    }

    return result;
  }, [muscles, selectedCategory, searchTerm, getMusclesByCategory]);

  // Ottiene le categorie disponibili
  const categories = React.useMemo(() => getCategories(), [getCategories]);

  // Ottiene i muscoli raggruppati per categoria
  const musclesGrouped = React.useMemo(() => getMusclesGroupedByCategory(), [getMusclesGroupedByCategory]);

  /**
   * Gestisce la selezione di un muscolo
   */
  const handleSelect = (muscleId) => {
    if (mode === 'single') {
      onChange(muscleId);
      setIsOpen(false);
    } else {
      // Modalità multipla
      const currentValue = Array.isArray(value) ? value : [];
      const newValue = currentValue.includes(muscleId)
        ? currentValue.filter(id => id !== muscleId)
        : [...currentValue, muscleId];
      onChange(newValue);
    }
  };

  /**
   * Verifica se un muscolo è selezionato
   */
  const isSelected = (muscleId) => {
    if (mode === 'single') {
      return value === muscleId;
    } else {
      return Array.isArray(value) && value.includes(muscleId);
    }
  };

  /**
   * Ottiene il testo da visualizzare nel campo di selezione
   */
  const getDisplayText = () => {
    if (mode === 'single') {
      if (!value) return placeholder;
      const muscle = muscles.find(m => m.id === value);
      return muscle ? muscle.name : placeholder;
    } else {
      const selectedCount = Array.isArray(value) ? value.length : 0;
      if (selectedCount === 0) return placeholder;
      if (selectedCount === 1) {
        const muscle = muscles.find(m => m.id === value[0]);
        return muscle ? muscle.name : placeholder;
      }
      return `${selectedCount} muscoli selezionati`;
    }
  };

  /**
   * Renderizza un muscolo nella lista
   */
  const renderMuscleItem = (muscle) => {
    const selected = isSelected(muscle.id);

    const itemSelected = selected
      ? isGym
        ? isDark
          ? 'bg-indigo-900/50 text-white'
          : 'bg-[#eeebf5] text-[#372d83]'
        : isDark
          ? 'bg-indigo-900 text-indigo-100'
          : 'bg-indigo-100 text-indigo-900'
      : isGym
        ? isDark
          ? 'text-white/90 hover:bg-gray-700'
          : 'text-[#3a3a3a] hover:bg-[#faf9fc]'
        : isDark
          ? 'hover:bg-gray-700 text-gray-200'
          : 'hover:bg-gray-100 text-gray-800';
    
    return (
      <div
        key={muscle.id}
        onClick={() => handleSelect(muscle.id)}
        className={`
          px-3 py-2 cursor-pointer transition-colors rounded
          ${itemSelected}
        `}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {mode === 'multi' && (
              <div className={`
                w-4 h-4 rounded border-2 flex items-center justify-center
                ${selected 
                  ? isGym
                    ? 'border-[#372d83] bg-[#372d83] dark:border-indigo-600 dark:bg-indigo-600'
                    : 'bg-indigo-600 border-indigo-600' 
                  : isDark 
                    ? isGym ? 'border-white/25' : 'border-gray-600' 
                    : isGym ? 'border-[#dad8e5]' : 'border-gray-300'
                }
              `}>
                {selected && <Check className="h-3 w-3 text-white" />}
              </div>
            )}
            <span className="font-medium">{muscle.name}</span>
          </div>
          {showHierarchy && muscle.parent_category && (
            <span className={`text-xs ${isGym ? (isDark ? 'text-white/50' : 'text-[#3a3a3a]/60') : (isDark ? 'text-gray-400' : 'text-gray-500')}`}>
              {muscle.parent_category}
            </span>
          )}
        </div>
      </div>
    );
  };

  /**
   * Renderizza la lista con gerarchia (categorie → muscoli)
   */
  const renderHierarchicalList = () => {
    return (
      <div className="space-y-3">
        {Object.entries(musclesGrouped).map(([cat, musclesInCategory]) => {
          // Se è selezionata una categoria specifica, mostra solo quella
          if (selectedCategory && cat !== selectedCategory) return null;

          return (
            <div key={cat}>
              <div className={`
                text-xs font-bold uppercase tracking-wide px-3 py-2
                ${isGym ? (isDark ? 'text-white/55' : 'text-[#3a3a3a]/70') : (isDark ? 'text-gray-400' : 'text-gray-600')}
              `}>
                {cat}
              </div>
              <div className="space-y-1">
                {musclesInCategory.map(muscle => renderMuscleItem(muscle))}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  /**
   * Renderizza la lista piatta (senza gerarchia)
   */
  const renderFlatList = () => {
    return (
      <div className="space-y-1">
        {filteredMuscles.map(muscle => renderMuscleItem(muscle))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`p-4 text-center ${isGym ? (isDark ? 'text-white/55' : 'text-[#3a3a3a]/70') : (isDark ? 'text-gray-400' : 'text-gray-600')}`}>
        Caricamento muscoli...
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {/* Label */}
      {label && (
        <label className={`block text-sm font-medium mb-2 ${isGym ? (isDark ? 'text-white/90' : 'text-[#3a3a3a]') : (isDark ? 'text-gray-200' : 'text-gray-700')}`}>
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Campo di selezione */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`
          w-full p-3 border rounded-lg cursor-pointer flex items-center justify-between
          transition-colors
          ${isGym
            ? isDark
              ? 'border-gray-700 bg-gray-800 text-white hover:border-white/25'
              : 'border-[#dad8e5] bg-white text-[#3a3a3a] hover:border-[#372d83]/35'
            : isDark 
              ? 'bg-gray-700 border-gray-600 text-white hover:border-gray-500' 
              : 'bg-white border-gray-300 text-gray-900 hover:border-gray-400'
          }
          ${isOpen
            ? isGym
              ? 'ring-2 ring-[#372d83]/45 dark:ring-[#c9c2e8]/30'
              : 'ring-2 ring-indigo-500'
            : ''}
        `}
      >
        <span className={!value || (Array.isArray(value) && value.length === 0)
          ? (isGym ? (isDark ? 'text-white/45' : 'text-[#3a3a3a]/45') : 'text-gray-400')
          : ''
        }>
          {getDisplayText()}
        </span>
        <ChevronDown className={`h-5 w-5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      {/* Dropdown */}
      {isOpen && (
        <>
          {/* Overlay per chiudere il dropdown */}
          <div 
            className="fixed inset-0 z-10" 
            onClick={() => setIsOpen(false)}
          />

          {/* Menu dropdown */}
          <div className={`
            absolute z-20 mt-2 w-full rounded-lg shadow-lg border
            max-h-96 overflow-hidden
            ${isGym
              ? isDark
                ? 'border-gray-700 bg-gray-900'
                : 'border-[#dad8e5] bg-white'
              : isDark 
                ? 'bg-gray-800 border-gray-700' 
                : 'bg-white border-gray-200'
            }
          `}>
            {/* Barra di ricerca */}
            <div className={`p-3 border-b ${isGym ? (isDark ? 'border-white/10' : 'border-[#dad8e5]') : (isDark ? 'border-gray-700' : 'border-gray-200')}`}>
              <div className="relative">
                <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isGym ? (isDark ? 'text-white/45' : 'text-[#3a3a3a]/50') : (isDark ? 'text-gray-400' : 'text-gray-500')}`} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t('userExerciseForm.muscleSelector.searchMuscle')}
                  className={`
                    w-full pl-10 pr-8 py-2 rounded border text-sm
                    ${isGym
                      ? isDark
                        ? 'border-gray-700 bg-gray-800 text-white placeholder:text-white/40'
                        : 'border-[#dad8e5] bg-white text-[#3a3a3a] placeholder:text-[#3a3a3a]/45'
                      : isDark 
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400' 
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    }
                  `}
                  onClick={(e) => e.stopPropagation()}
                />
                {searchTerm && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSearchTerm('');
                    }}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Filtro categorie (se showHierarchy è true) */}
            {showHierarchy && !category && (
              <div className={`p-3 border-b ${isGym ? (isDark ? 'border-white/10' : 'border-[#dad8e5]') : (isDark ? 'border-gray-700' : 'border-gray-200')}`}>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCategory('');
                    }}
                    className={`
                      px-3 py-1 rounded text-xs font-medium transition-colors
                      ${!selectedCategory
                        ? isGym
                          ? isDark
                            ? 'bg-indigo-600 text-white'
                            : 'bg-[#372d83] text-white'
                          : isDark 
                            ? 'bg-indigo-900 text-indigo-100' 
                            : 'bg-indigo-600 text-white'
                        : isGym
                          ? isDark
                            ? 'bg-gray-800 text-white/85 hover:bg-gray-700/80'
                            : 'bg-[#faf9fc] text-[#3a3a3a] ring-1 ring-[#dad8e5] hover:bg-[#eeebf5]'
                          : isDark
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }
                    `}
                  >
{t('userExerciseForm.muscleSelector.allCategories')}
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCategory(cat);
                      }}
                      className={`
                        px-3 py-1 rounded text-xs font-medium transition-colors
                        ${selectedCategory === cat
                          ? isGym
                            ? isDark
                              ? 'bg-indigo-600 text-white'
                              : 'bg-[#372d83] text-white'
                            : isDark 
                              ? 'bg-indigo-900 text-indigo-100' 
                              : 'bg-indigo-600 text-white'
                          : isGym
                            ? isDark
                              ? 'bg-gray-800 text-white/85 hover:bg-gray-700/80'
                              : 'bg-[#faf9fc] text-[#3a3a3a] ring-1 ring-[#dad8e5] hover:bg-[#eeebf5]'
                            : isDark
                              ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }
                      `}
                    >
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Lista muscoli */}
            <div className="max-h-64 overflow-y-auto p-2">
              {filteredMuscles.length === 0 ? (
                <div className={`p-4 text-center text-sm ${isGym ? (isDark ? 'text-white/55' : 'text-[#3a3a3a]/70') : (isDark ? 'text-gray-400' : 'text-gray-600')}`}>
                  Nessun muscolo trovato
                </div>
              ) : showHierarchy && !searchTerm ? (
                renderHierarchicalList()
              ) : (
                renderFlatList()
              )}
            </div>

            {/* Footer con azioni (solo per modalità multi) */}
            {mode === 'multi' && (
              <div className={`p-3 border-t ${isGym ? (isDark ? 'border-white/10' : 'border-[#dad8e5]') : (isDark ? 'border-gray-700' : 'border-gray-200')}`}>
                <div className="flex justify-between items-center">
                  <span className={`text-sm ${isGym ? (isDark ? 'text-white/55' : 'text-[#3a3a3a]/70') : (isDark ? 'text-gray-400' : 'text-gray-600')}`}>
{Array.isArray(value) ? value.length : 0} {t('userExerciseForm.muscleSelector.selectedCount')}
                  </span>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange([]);
                      }}
                      className={`
                        px-3 py-1 rounded text-xs font-medium transition-colors
                        ${isGym
                          ? isDark
                            ? 'bg-gray-800 text-white/90 hover:bg-gray-700'
                            : 'bg-[#faf9fc] text-[#3a3a3a] ring-1 ring-[#dad8e5] hover:bg-[#eeebf5]'
                          : isDark
                            ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }
                      `}
                    >
{t('userExerciseForm.muscleSelector.deselectAll')}
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsOpen(false);
                      }}
                      className={`
                        px-3 py-1 rounded text-xs font-medium transition-colors
                        ${isGym
                          ? isDark
                            ? 'bg-indigo-600 text-white hover:brightness-110'
                            : 'bg-[#372d83] text-white hover:bg-[#2d2468]'
                          : isDark
                            ? 'bg-indigo-900 text-indigo-100 hover:bg-indigo-800'
                            : 'bg-indigo-600 text-white hover:bg-indigo-700'
                        }
                      `}
                    >
{t('userExerciseForm.muscleSelector.confirm')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default MuscleGroupSelector;

